import type { CheckBatchStatus, CheckReport, CheckTarget } from '@/types/check';
import type { AppLocale, LibraryItemKind } from '@/types';

/**
 * CheckService — 素材內容檢查(批次檢查 / 檢查報告 / 文案套用)。
 * -----------------------------------------------------------------------------
 * 端點全表 = `creative-test-v2/docs/88-asset-check-api-reference.md`(§ 標在各方法上):
 *   - `POST /asset_check/jobs`(批次;回 job_id + firestore_paths)
 *   - `GET  /asset_check/jobs`(§1.5 列出**進行中**批次 — 冷啟還原,不必先知道 job_id)
 *   - `GET  /asset_check/jobs/{jid}`(cold-start;即時進度走 Firestore listener)
 *   - `POST /asset_check/jobs/{jid}/cancel`(冪等)
 *   - `GET  /asset_libraries/{lib}/items/{iid}/check-report`(404 = 未檢查;`?report_id=`)
 *   - `POST /asset_libraries/{lib}/items/{iid}/check`(單素材重檢,扣 1 點)
 *   - `POST …/check-report/copy-apply`(套用寫回 applied_texts;null = 復原)
 *   - `POST …/check/group-items`(整組判定重跑)
 *
 * 真實注入 = **httpCheckService**;`fixtureCheckService` 只在測試與 demo flag
 * (`hai.checkDemo`)下出現。**介面語意兩實作必須逐條相同**(否則換實作即行為變化):
 *   - `getReport`:未檢查回 **null**(http 版把 404 映射成 null)
 *   - `getBatch`:查無(reload 後 in-memory 遺失 / job 不存在)回 null → store 清 stale
 *   - `listActiveBatches`:沒有進行中的批次回 **空陣列**(不是 null、不是 404)
 *   - `cancelBatch`:冪等;**保留已完成 item 的結果與計數**
 *   - `applyCopy(text=null)` = 復原(清 applied_texts 該 key)
 *   - `recheckGroupItems` **resolve 的就是新報告本身,直接用它**(BE 建新 doc / 新
 *     report_id,只把舊 doc 的 group_stale 清掉)—— 別「重取報告」,指名舊 id 會拿回
 *     重檢前那一份,燈號看起來完全沒變而使用者已被扣 1 點
 */

/**
 * startBatch 的 target — `kind` / `name` / `made` 是 **fixture-only 顯示 hint**
 * (挑樣本報告 + 疊真實 item 身分欄)。**http 實作一律剝除**:docs/88 §1 的
 * `targets[]` 只收 `lib_id` / `item_id`,BE model 是 `extra="forbid"`,多送一個鍵
 * 就是整批裸 422。
 */
export interface CheckStartTarget extends CheckTarget {
  kind?: LibraryItemKind;
  name?: string;
  made?: string;
}

/**
 * 送出重送保護用的冪等鍵(docs/88 §1 §5:≤100 字、`[A-Za-z0-9_-]`)。
 * **不帶就完全沒有重送保護** — double-click 會建兩個 job、扣兩份點數。
 */
export interface CheckIdempotentOpts {
  idempotencyKey?: string;
  /**
   * R424.1(BE docs/137 §5)— 報告語系;只有 `startBatch` / `checkSingle` 會送(整組重檢不收)。
   * **只在 catalog `features.asset_check_locale` 時帶**(`resolveAssetCheckLocale()`;舊 BE
   * `extra="forbid"` → 422)。同一把冪等鍵換語系重送 = 409 → 呼叫端用 `keyForScope`。
   */
  locale?: AppLocale;
}

export interface CheckService {
  /**
   * 取檢查報告;**未檢查回 null**(= 404 `CHECK_REPORT_NOT_FOUND` 語意)。
   *
   * `reportId`(docs/88 §4 query `report_id`)—— 不帶 = 取最新一份。
   * ⚠️ **拿得到就一定要帶**:docs/87 §9 明言 `firestore:indexes` 尚未部署
   * (CI 沒有這個 job,每次都要人工跑),在那之前「查最新」會撞缺索引錯誤 ——
   * 那不是 404,不會被轉成「未檢查」,而是整個報告頁的錯誤態。
   * 現成來源 = `CheckBatchStatus.reportIds[checkTargetKey(target)]`。
   */
  getReport(target: CheckTarget, reportId?: string): Promise<CheckReport | null>;
  /**
   * 啟動批次檢查(reserve 1 點/素材)。**回應本身就是第一份快照**(docs/88 §2.1)
   * → 回 `CheckBatchStatus`,呼叫端直接 `setBatch`,不必再打一趟 `getBatch`。
   * fixture 版在 demo flag 關閉時 reject。
   */
  startBatch(targets: CheckStartTarget[], opts?: CheckIdempotentOpts): Promise<CheckBatchStatus>;
  /** cold-start 取批次狀態;查無回 null。 */
  getBatch(batchId: string): Promise<CheckBatchStatus | null>;
  /**
   * 列出目前工作區中**自己建立、還在跑**的批次(`PENDING`/`RUNNING`;新到舊)——
   * docs/88 §1.5。**不扣點**,rate limit 60/min,沒有進行中的批次回**空陣列**。
   *
   * 用途 = **冷啟還原**:批次是在別台裝置按的、清過站台資料、或超過
   * `checkBatchStore` 的 30 分 TTL 被 prune 掉時,`getBatch` 需要 batchId 而我們
   * 已經沒有了 → 沒有這一支就完全看不到在跑的批次,使用者會再按一次「開始檢查」
   * 把同一批素材再扣一次點(idempotency key 是新鑄的,擋不住)。
   * 每筆都含 `firestore_paths` → 拿回來直接 `setBatch` 就能接回 listener。
   *
   * @param limit 1–20(預設 5;超界由實作夾取)。
   */
  listActiveBatches(limit?: number): Promise<CheckBatchStatus[]>;
  /**
   * 冪等取消(未完成件逐件退點,已完成保留報告與扣點)。
   * **回應 = 最新快照**(§3)→ 回 status 讓呼叫端直接寫回;404 視同已取消 → null。
   */
  cancelBatch(batchId: string): Promise<CheckBatchStatus | null>;
  /**
   * 訂閱批次進度(fixture = 內部 timer 推;http = 回 no-op,即時進度由
   * `useCheckBatchListener` 走 Firestore onSnapshot 兩路 —— repo listener-only
   * 慣例,**不退 polling**〔docs/88 §2.3 明禁〕)。回傳 unsubscribe。
   */
  subscribeBatch(batchId: string, cb: (status: CheckBatchStatus) => void): () => void;
  /** 單素材重檢(獨立端點,**再扣 1 點**;回應 = 1-item job 快照)。 */
  checkSingle(
    target: CheckStartTarget,
    opts?: CheckIdempotentOpts,
  ): Promise<CheckBatchStatus | null>;
  /**
   * 校對稿套用/復原 —— **整批 map**:key = `"<field_k>:<rowIdx>"`(0-based,field_k
   * 是 BE `CopyCategory`),值 `null` = 復原。回應 = **更新後的完整報告**,呼叫端
   * 直接拿它刷新 `appliedTexts` / `groupStale`(不必再 GET)。
   */
  applyCopy(
    target: CheckTarget,
    applied: Record<string, string | null>,
    reportId?: string,
  ): Promise<CheckReport | null>;
  /**
   * 整組判定項重跑(**扣 1 點**)。前置 `report.groupStale === true`,否則 409
   * `CHECK_GROUP_ITEMS_NOT_STALE`;**跑完 BE 清旗標,按第二次必 409**。
   * 回應 = 新報告(不必再 GET)。
   */
  recheckGroupItems(target: CheckTarget, reportId?: string): Promise<CheckReport | null>;
}

/**
 * 檢查 demo(`fixtureCheckService` 樣本報告 + 模擬批次)是否啟用 —— **純 opt-in**:
 * `localStorage.setItem('hai.checkDemo','1')` + reload(比照 JOBS_DEBUG 慣例),
 * 所有環境(含 `pnpm dev`)預設 **關**。
 *
 * ⚠️ **刻意沒有 `import.meta.env.DEV` 預設開**(R213 移除):真 BE 上線後,dev 預設
 * 開 demo = 本機永遠打不到 `app/asset_check/`,「不用 MOCK」直接失守,而且假報告
 * 還帶著「會用 N 次額度」的承諾(panel BA 條款)。
 *
 * 這支**只決定注入哪個 impl**([services/index.ts](./index.ts))與 fixture 自己的
 * 假跑守衛。功能可不可用是另一支 —— 見 `CHECK_SERVICE_SUPPORTED`。
 */
export function isCheckDemoEnabled(): boolean {
  try {
    return localStorage.getItem('hai.checkDemo') === '1';
  } catch {
    return false;
  }
}

/**
 * 檢查域後端(`app/asset_check/`)是否已交付 —— **「執行檢查」真實可用性的唯一開關**
 * (比照 `assetLibrariesService.ts` 的 `ASSET_ITEM_MUTATION_SUPPORTED`)。
 *
 * ⚠️ 別拿 `isCheckDemoEnabled()` 當這個用:那支是 **fixture demo** 的開關(純 opt-in,
 * 決定注入哪個 impl)。兩者混用的後果是靜默的 —— 只換注入而沒翻這裡,dev/beta/prod
 * 三個部署環境的三個「執行檢查」入口(批次列 / 右鍵選單 / 燈箱)仍然 disabled、
 * tooltip 還顯「待後端支援」,而 BE 端點已經活著,測試也全綠。
 *
 * R213(2026-08-07)= `true`:`app/asset_check/` P1 全數 ship + dev 已部署,
 * [services/index.ts](./index.ts) 注入 `createHttpCheckService`。
 * 要整域退場(BE 出事下架功能)就把這裡翻 `false` —— UI 三個入口一起變灰,
 * 不必動元件。
 */
export const CHECK_SERVICE_SUPPORTED = true;
